import { useRef } from "react";
import { toast } from "sonner";

import { useUpdateClientMutation } from "Clients/Api/Mutations/useUpdateClientMutation";
import { ClientForm, type ClientFormHandle } from "Clients/Forms/ClientForm";
import {
  type ClientFormData,
  toSaveClientRequest,
} from "Clients/Forms/clientFormSchema";
import type { ClientDetailDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlModal } from "Shared/Dialogs/BlModal";

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: ClientDetailDto;
  clientID: string;
}

export const EditClientModal = ({
  isOpen,
  onClose,
  client,
  clientID,
}: EditClientModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<ClientFormHandle | null>(null);
  const updateClientMutation = useUpdateClientMutation();

  const onSubmit = async (formData: ClientFormData) => {
    try {
      await updateClientMutation.mutateAsync({
        clientID,
        data: toSaveClientRequest(formData),
      });
      toast.success(t(Resources.Clients.EditClientModal.SuccessMessage));
      onClose();
    } catch {
      // field errors shown in form, non-field errors shown as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Clients.EditClientModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Clients.EditClientModal.Save)}
      maxWidth="800px"
    >
      <ClientForm
        client={client}
        formRef={formRef}
        serverError={updateClientMutation.error}
      />
    </BlModal>
  );
};
